import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Sparkles, Loader2, Heart, RefreshCw } from "lucide-react";

const ROLE = { tutina: "Tutina / body", accessorio: "Accessorio", copertina: "Copertina" };

const LookItem = ({ it, anchor }) => (
  <Link to={`/prodotto/${it.id}`} data-testid={`look-item-${it.id}`}
    className="group rounded-2xl bg-white border border-[#EAE3DC] overflow-hidden flex flex-col hover:-translate-y-0.5 transition-transform">
    <div className="relative aspect-square overflow-hidden bg-[#F4EFEA]">
      {anchor && <span data-testid="look-anchor-badge" className="absolute top-2 left-2 z-10 rounded-full bg-[#2D2825] text-white text-[10px] px-2 py-0.5">Questo capo</span>}
      {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
    </div>
    <div className="p-3 flex flex-col gap-1 flex-1">
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#8C7F78]">{ROLE[it.role]}</span>
      <span className="text-sm text-[#2D2825] line-clamp-2">{it.title}</span>
      <div className="mt-auto pt-1 flex flex-col gap-1">
        {it.color && <span className="self-start max-w-full truncate rounded-full bg-[#F4EFEA] px-2 py-0.5 text-[11px] text-[#5C534E]" data-testid={`look-color-${it.id}`}>{it.color}</span>}
        <span className="font-serif text-lg text-[#C87D75]">€ {it.price}</span>
      </div>
    </div>
  </Link>
);

export const CoordinatedLook = ({ productId, heading = "Il look coordinato per te", testId = "coordinated-look" }) => {
  const { profile, addManyToWishlist } = useProfile();
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (refresh = false) => {
    if (!profile) return;
    setLoading(true);
    api.get(`/look/${profile.id}`, { params: { product_id: productId || undefined, refresh: refresh || undefined } })
      .then(({ data }) => setLook(data))
      .catch(() => setLook(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [profile?.id, profile?.baby_name, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile || (!loading && !look?.available)) return null;
  const total = (look?.items || []).reduce((s, i) => s + (parseFloat(i.price) || 0), 0);

  return (
    <section id={productId ? undefined : "look"} data-testid={testId} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 scroll-mt-20">
      <div className="rounded-3xl bg-[#FBF3EE] border border-[#F0DDD3] p-4 sm:p-10 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[#C87D75]">
              <Sparkles size={14} /> {heading}
            </span>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-[#2D2825]" data-testid="look-title">{look?.title || "Stiamo abbinando i colori…"}</h2>
            {look?.description && <p className="mt-2 text-sm text-[#5C534E] max-w-xl">{look.description}</p>}
          </div>
          <button onClick={() => load(true)} disabled={loading} data-testid="look-refresh"
            className="self-start sm:self-auto inline-flex items-center gap-2 text-sm text-[#8C7F78] hover:text-[#2D2825] transition-colors disabled:opacity-50">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Un altro look
          </button>
        </div>
        {loading && !look ? (
          <div className="flex items-center gap-2 text-[#8C7F78] py-10 justify-center"><Loader2 className="animate-spin" size={18} /> L'AI sta creando il look…</div>
        ) : (
          <>
            <div className="mb-rail sm:grid sm:grid-cols-3 gap-3 sm:gap-6">
              {look.items.map((it) => <LookItem key={it.id} it={it} anchor={productId && String(it.id) === String(productId)} />)}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-sm text-[#5C534E] text-center sm:text-left">Stagione {look.season} · taglia {look.sizes?.[0]} · totale <strong data-testid="look-total">€ {total.toFixed(2)}</strong></span>
              <button data-testid="look-add-all" onClick={() => addManyToWishlist(look.items.map((i) => ({ ...i, color_chosen: !!i.color })))}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white px-6 min-h-[48px] text-sm transition-colors">
                <Heart size={15} /> Aggiungi il look alla lista
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
